import * as React from 'react';
import { Add, EditOutlined } from '@mui/icons-material';
import { Button, ListSubheader, MenuItem, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { MouseEvent, ReactNode, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UIContext } from '../context/UIContext';
import { CustomTimePeriod } from '../api/customTimePeriods';

type CustomTimePeriodMenuItemsProps = {
  customPeriods: CustomTimePeriod[];
  onSelect: (period: CustomTimePeriod) => void;
  onEdit: (period: CustomTimePeriod) => void;
  onCreate: () => void;
};

export default function useCustomTimePeriodMenuItems({
  customPeriods,
  onSelect,
  onEdit,
  onCreate,
}: CustomTimePeriodMenuItemsProps): ReactNode[] {
  const { t } = useTranslation();
  const theme = useTheme();
  const { colorMode } = useContext(UIContext);
  const borderColor =
    colorMode === 'light' ? theme?.palette?.grey?.['12p'] : theme?.palette?.grey?.['23p'];

  const stopSelectPropagation = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return useMemo(
    () => [
      <ListSubheader
        key="custom-periods-header"
        sx={{
          borderTop: `1px ${borderColor} solid`,
          padding: '16px 16px 8px',
          backgroundColor: 'background.paper',
        }}
      >
        <Typography variant="h3">{t('components.datePicker.customTimePeriods')}</Typography>
        <Typography variant="body2" sx={{ marginBottom: '8px' }}>
          {t('components.datePicker.saveSpecificDateRanges')}
        </Typography>
      </ListSubheader>,
      ...customPeriods.map((customPeriod) => (
        <MenuItem
          sx={{ display: 'flex', justifyContent: 'space-between' }}
          value={customPeriod.id}
          key={`custom-period-${customPeriod.id}`}
          onClick={() => onSelect(customPeriod)}
        >
          {customPeriod.name}
          <Button
            sx={{
              minWidth: 0,
              minHeight: 0,
              padding: 0.5,
              color: 'text.secondary',
              ':hover': {
                color: 'text.primary',
                backgroundColor: colorMode === 'light' ? 'main.8p' : 'main.26p',
              },
              borderRadius: '8px',
              '.MuiSelect-select &': {
                display: 'none',
              },
            }}
            onClick={(event) => {
              stopSelectPropagation(event);
              onEdit(customPeriod);
            }}
            onMouseDown={stopSelectPropagation}
            data-qa-id={`editCustomPeriod-${customPeriod.id}`}
          >
            <EditOutlined fontSize="small" />
          </Button>
        </MenuItem>
      )),
      <Box
        key="custom-periods-add"
        sx={{
          width: '100%',
          borderTop: `1px ${borderColor} solid`,
        }}
      >
        <Button
          sx={{ width: '100%', justifyContent: 'flex-start', paddingX: 2 }}
          onClick={(event) => {
            stopSelectPropagation(event);
            onCreate();
          }}
          onMouseDown={stopSelectPropagation}
          data-qa-id="newCustomTimePeriod"
        >
          <Add fontSize="small" sx={{ marginRight: 0.5 }} />
          {t('components.datePicker.newCustomTimePeriod')}
        </Button>
      </Box>,
    ],
    [borderColor, colorMode, customPeriods, onCreate, onEdit, onSelect, t, theme.palette.text.secondary]
  );
}
